import { useEffect, useRef } from 'react'
import { useMeetingManager, useAudioVideo } from 'amazon-chime-sdk-component-library-react'

import type { ChimeEvent } from '../types/chime-observability'

// Constants for AWS best practices
const FAILURE_EVENTS = new Set(['audioInputFailed', 'videoInputFailed', 'meetingStartFailed', 'meetingFailed'])

const MEETING_HISTORY_TIME_WINDOW_MS = 5 * 60 * 1000 // 5 minutes
const BATCH_SEND_INTERVAL_MS = 2 * 60 * 1000 // 2 minutes

const API_ENDPOINT_URL = import.meta.env.VITE_CHIME_OBSERVABILITY_ENDPOINT_URL

/**
 * Filters meeting history to include only events from the last 5 minutes.
 * Used for failure events to reduce payload size while maintaining diagnostic value.
 *
 * @param meetingHistory - Array of meeting history events with timestamps
 * @returns Filtered array containing only recent events (last 5 minutes)
 */
function filterRecentMeetingHistory(
  meetingHistory: { name: string; timestampMs: number }[]
): { name: string; timestampMs: number }[] {
  const now = Date.now()
  return meetingHistory.filter(({ timestampMs }) => now - timestampMs < MEETING_HISTORY_TIME_WINDOW_MS)
}

/**
 * Attaches observability to Chime meeting session.
 * Collects meeting events following AWS best practices and sends them to API endpoint.
 *
 * @remarks
 * **AWS Best Practices Compliance:**
 * - Failure events (`audioInputFailed`, `videoInputFailed`, `meetingStartFailed`, `meetingFailed`):
 *   Include meetingHistory filtered to last 5 minutes for diagnostics
 * - All other events: Exclude meetingHistory to minimize payload size
 *
 * **Behavior:**
 * - Failure events: Sent immediately for quick diagnostics
 * - Regular events: Sent in batches every 2 minutes
 * - All remaining events: Sent when meeting ends
 * - Uses fire-and-forget POST (no retry logic)
 * - Logs events to console for debugging
 *
 * @see https://aws.amazon.com/blogs/business-productivity/monitoring-and-troubleshooting-with-amazon-chime-sdk-meeting-events/
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   useChimeObservability();
 *   return <div>Meeting content</div>;
 * }
 * ```
 */
export default function useChimeObservability() {
  const audioVideo = useAudioVideo()
  const meetingManager = useMeetingManager()
  const eventsRef = useRef<ChimeEvent[]>([])
  const hasSetupObserverRef = useRef(false)

  useEffect(() => {
    const { meetingSession } = meetingManager

    // Guard clause: audioVideo not ready
    if (!audioVideo) {
      console.warn('[ChimeObservability] AudioVideo not available yet')
      return undefined
    }

    // Guard clause: no meeting session (defensive, shouldn't happen if audioVideo exists)
    if (!meetingSession) {
      console.warn('[ChimeObservability] No meeting session available')
      return undefined
    }

    // Guard clause: no API endpoint configured
    if (!API_ENDPOINT_URL) {
      console.warn('[ChimeObservability] No API endpoint configured (VITE_CHIME_OBSERVABILITY_ENDPOINT_URL)')
      return undefined
    }

    // Guard clause: prevent double setup (React StrictMode)
    if (hasSetupObserverRef.current) {
      console.warn('[ChimeObservability] Observer already setup, skipping')
      return undefined
    }

    console.log('[ChimeObservability] Setting up observability')

    // Function to send events to API
    const sendEvents = (reason: string) => {
      // Capture and clear buffer atomically to prevent race conditions
      const eventsToSend = eventsRef.current
      eventsRef.current = []

      if (eventsToSend.length === 0) {
        console.log(`[ChimeObservability] No events to send (${reason})`)
        return
      }

      console.log(`[ChimeObservability] Sending ${eventsToSend.length} events to ${API_ENDPOINT_URL} (${reason})`)

      // Send events to API
      fetch(API_ENDPOINT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventsToSend)
      })
        .then(() => {
          console.log(`[ChimeObservability] Events sent successfully (${reason})`)
        })
        .catch((error) => {
          console.error(`[ChimeObservability] Failed to send events (${reason}):`, error)
        })
    }

    // Event observer - collects all Chime SDK events
    const eventObserver = {
      eventDidReceive(name: string, attributes: Record<string, unknown>) {
        // Destructure meetingHistory from attributes for separate handling
        const { meetingHistory, ...otherAttributes } = attributes

        let processedAttributes: Record<string, unknown>

        // AWS Best Practice: Handle failure events differently
        if (FAILURE_EVENTS.has(name)) {
          // Failure events: include filtered meetingHistory (last 5 minutes)
          if (meetingHistory && Array.isArray(meetingHistory)) {
            const filteredHistory = filterRecentMeetingHistory(meetingHistory)

            console.log(
              `[ChimeObservability] Failure event "${name}" - Filtering meetingHistory from ${meetingHistory.length} to ${filteredHistory.length} events (last 5 minutes)`
            )

            processedAttributes = {
              ...otherAttributes,
              meetingHistory: filteredHistory
            }
          } else {
            // No history to filter
            processedAttributes = otherAttributes
          }
        } else {
          // Non-failure events: exclude meetingHistory entirely
          processedAttributes = otherAttributes

          if (meetingHistory) {
            console.log(
              `[ChimeObservability] Non-failure event "${name}" - Excluding meetingHistory (${Array.isArray(meetingHistory) ? meetingHistory.length : 'N/A'} events)`
            )
          }
        }

        const event: ChimeEvent = { name, attributes: processedAttributes }

        // Log processed event for debugging
        console.log('[ChimeObservability] Event processed:', name, processedAttributes)

        // Accumulate event in ref
        eventsRef.current.push(event)

        // Send immediately if it's a failure event
        if (FAILURE_EVENTS.has(name)) {
          console.log(`[ChimeObservability] Failure event detected, sending immediately`)
          setTimeout(() => sendEvents(`failure event: ${name}`), 0)
        }
      }
    }

    // AudioVideo observer - detects meeting end
    const audioVideoObserver = {
      audioVideoDidStop: () => {
        console.log('[ChimeObservability] Meeting ended, preparing to send events')
        // Use setTimeout for fire-and-forget async execution
        setTimeout(() => sendEvents('meeting ended'), 0)
      }
    }

    // Attach observers to Chime SDK
    meetingSession.eventController.addObserver(eventObserver)
    meetingSession.audioVideo.addObserver(audioVideoObserver)
    hasSetupObserverRef.current = true

    // Setup periodic batch sending
    const batchIntervalId = setInterval(() => {
      sendEvents('periodic batch')
    }, BATCH_SEND_INTERVAL_MS)

    console.log(`[ChimeObservability] Batch sending enabled (every ${BATCH_SEND_INTERVAL_MS / 1000 / 60} minutes)`)

    // Handle page unload - send remaining events via beacon
    const handlePageUnload = () => {
      // Capture and clear buffer atomically to prevent race conditions
      const events = eventsRef.current
      eventsRef.current = []

      if (events.length === 0) {
        return
      }

      console.log(`[ChimeObservability] Page unloading, sending ${events.length} events via beacon`)

      // Use sendBeacon for reliable delivery during page unload
      const blob = new Blob([JSON.stringify(events)], { type: 'application/json' })
      navigator.sendBeacon(API_ENDPOINT_URL, blob)
    }

    // Handle visibility change - send events when page becomes hidden
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handlePageUnload()
      }
    }

    // Listen for page visibility changes and beforeunload
    window.addEventListener('beforeunload', handlePageUnload)
    window.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup function - remove observers on unmount
    return () => {
      console.log('[ChimeObservability] Cleaning up observers')
      window.removeEventListener('beforeunload', handlePageUnload)
      window.removeEventListener('visibilitychange', handleVisibilityChange)
      clearInterval(batchIntervalId)
      meetingSession.eventController.removeObserver(eventObserver)
      meetingSession.audioVideo.removeObserver(audioVideoObserver)
      hasSetupObserverRef.current = false
    }
  }, [audioVideo, meetingManager])
}

export const ChimeObservabilityProvider = () => {
  useChimeObservability()
  return null
}
